'use client'

import { useContext, useEffect, useState } from 'react'

import { EnsureBalanceActivated } from '@marketplace-web/balance-activation/balance-activation-feature'
import { ReturnShippingOptionsModal } from '@marketplace-web/complaints/complaints-feature'
import {
  GetShippingLabelContext,
  useDropOffPointMapContext,
} from '@marketplace-web/shipping-services-provider/shipping-services-provider-feature'
import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'
import { TaxpayerRestrictionInfoModal } from '@marketplace-web/taxpayers/taxpayers-feature'
import { useAbTest, useTrackAbTestCallback } from '@marketplace-web/feature-flags/ab-tests-data'
import { useFetch, ResponseCode } from '@marketplace-web/core-api/api-client-util'
import { reloadPage } from '@marketplace-web/browser/browser-navigation-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useLatestCallback, UiState } from '@marketplace-web/shared/ui-state-util'
import {
  GenerateLabelModal,
  DropOffTypeKey,
  orderShippingLabel,
  clickEvent,
  PackagingTypes,
  ModalOrigin,
} from '@marketplace-web/conversation-shipping/conversation-shipping-data'
import { TransactionSide } from '@marketplace-web/messaging/conversation-messaging-data'
import {
  getSnadComplaint,
  transformComplaintDto,
} from '@marketplace-web/complaints/complaints-data'
import {
  getDefaultShippingAddress,
  transformDefaultShippingAddress,
} from '@marketplace-web/address/address-data'
import { ShippingContactType } from '@marketplace-web/shipping/shipping-contact-data'
import {
  ConversationActionProps,
  ConversationActionType,
} from '@marketplace-web/messaging/conversation-action-data'
import { ConversationActionButton } from '@marketplace-web/messaging/conversation-action-feature'
import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'

import GenerateShippingLabelModal from '../GenerateShippingLabelModal'
import CourierPickUpDateSelectionModal from '../GenerateShippingLabelModal/CourierPickUpDateSelectionModal'
import CarrierChangeModal from '../CarrierChangeModal'
import LabelTypeSelectionModal from '../LabelTypeSelectionModal'
import DropOffTypeConfirmationModal from '../DropOffTypeConfirmationModal'
import DropOffTypeSelectionModal from '../DropOffTypeSelectionModal'
import DropOffPointModal from '../DropOffPointModal'
import DropOffPointTypeSelectionDialog from '../DropOffPointTypeSelectionDialog'
import GenericErrorModal from '../GenericErrorModal'
import AddressModal from '../AddressModal'
import ShippingContactModal from '../ShippingContactModal'
import {
  isReturnShipment,
  isPackageTypeLabelTypeFlow,
  isDigitalLabelFlowEnabled,
  isOldDropOffPointTypeFlow,
  isDropOffTypeFlow,
  isReusablePackagingFlowEnabled,
  getDropOffPointType,
  addressFieldConfiguration,
  hasMultipleLabelTypesAcrossAllDropOffs,
} from '../../utils/generate-label'

const GetShippingLabel = ({
  action,
  title,
  primary,
  size,
  theme,
  inverse,
}: ConversationActionProps) => {
  const { track } = useTracking()
  const { conversation } = useConversationContext()
  const trackAbTest = useTrackAbTestCallback()
  const dropOffTypeAndPackagingRewrite = useAbTest('dd_drop_off_type_and_packaging_rewrite')
  const isDropOffTypeAndPackagingRewriteOn = dropOffTypeAndPackagingRewrite?.variant === 'on'
  const isLabelOptionsRewriteFSEnabled = useFeatureSwitch('web_label_options_rewrite')
  const dropOffAndLabelTypeSelectionAbTest = useAbTest(
    'drop_off_and_label_type_selection_in_get_shipping_label_flow',
  )
  const isDropOffAndLabelTypeSelectionAbTestOn =
    dropOffAndLabelTypeSelectionAbTest?.variant === 'on'

  const transactionId = conversation?.transaction?.id
  const isBundle = conversation?.transaction?.isBundle
  const shipmentId = conversation?.transaction?.shipment?.id
  const userSide = conversation?.transaction?.userSide

  const {
    state: {
      collectionDates: { confirmedCollectionDate, confirmedCollectionTime, fetchCollectionDates },
      dropOffTypeSelection: { selectedDropOffTypeKey },
      labelTypeSelection: { selectedLabelType },
      activeModal: { activeModal, setActiveModal },
      dropOffPointTypeSelection: { selectedDropOffPointType },
      labelOptions: {
        fetchLabelOptions,
        labelOptions,
        labelOptionsUiState,
        labelOptionsError,
        labelOptionsRewrite,
      },
    },
  } = useContext(GetShippingLabelContext)
  const { clearNearbyDropOffPoints } = useDropOffPointMapContext()

  const labelOptionsData = labelOptions || labelOptionsRewrite

  const [isShippingLabelLoading, setIsShippingLabelLoading] = useState(false)
  const [openModal, setOpenModal] = useState(false)
  const [modalOrigin, setModalOrigin] = useState<null | ModalOrigin>(null)

  const {
    fetch: fetchSnadComplaint,
    transformedData: complaint,
    isLoading: isComplaintLoading,
  } = useFetch(getSnadComplaint, transformComplaintDto)

  const { fetch: fetchDefaultShippingAddress, transformedData: defaultShippingAddress } = useFetch(
    getDefaultShippingAddress,
    transformDefaultShippingAddress,
  )

  useEffect(() => {
    fetchDefaultShippingAddress()
  }, [fetchDefaultShippingAddress])

  const isMyOwnPackagingExists = !!labelOptions?.dropOffTypes?.find(
    ({ key }) => key === DropOffTypeKey.MyOwnPackage,
  )

  const hasReusablePackaging =
    labelOptionsRewrite?.dropOffs?.some(dropOff =>
      dropOff.options?.some(option => option.freeReusablePackagingAvailable),
    ) ?? false

  const fetchComplaint = () => {
    if (!transactionId) return

    fetchSnadComplaint(transactionId)
  }

  const fetchLabelOptionsData = () => {
    if (!shipmentId) return

    fetchLabelOptions({
      shipmentId,
      isLabelOptionsRewriteFSEnabled:
        isDropOffTypeAndPackagingRewriteOn && isLabelOptionsRewriteFSEnabled,
    })
  }

  const trackDropOffTypeAndPackagingRewriteAbTestExposure = useLatestCallback(() => {
    if (userSide === TransactionSide.Buyer) return

    trackAbTest(dropOffTypeAndPackagingRewrite)
  })

  useEffect(() => {
    if (!openModal || labelOptionsUiState === UiState.Pending) return

    if (labelOptionsError) {
      setActiveModal(GenerateLabelModal.GenericErrorModal)

      return
    }

    trackDropOffTypeAndPackagingRewriteAbTestExposure()

    if (isOldDropOffPointTypeFlow(labelOptions)) {
      setActiveModal(GenerateLabelModal.DropOffPointTypeSelectionModal)

      return
    }

    if (labelOptionsRewrite) {
      const hasMultipleLabelTypes = hasMultipleLabelTypesAcrossAllDropOffs(
        labelOptionsRewrite,
        isDropOffAndLabelTypeSelectionAbTestOn,
      )
      const hasMultipleDropOffs = isDropOffTypeFlow(labelOptionsRewrite, labelOptions)

      if (hasMultipleDropOffs) {
        setActiveModal(GenerateLabelModal.DropOffTypeSelectionModal)

        return
      }

      if (hasMultipleLabelTypes) {
        setActiveModal(GenerateLabelModal.DigitalLabelTypeSelectionModal)

        return
      }
    }

    if (isDropOffTypeFlow(labelOptionsRewrite, labelOptions)) {
      setActiveModal(GenerateLabelModal.DropOffTypeSelectionModal)

      return
    }

    if (isDigitalLabelFlowEnabled(labelOptions, labelOptionsRewrite)) {
      setActiveModal(GenerateLabelModal.DigitalLabelTypeSelectionModal)

      return
    }

    if (hasReusablePackaging) {
      setActiveModal(GenerateLabelModal.DropOffTypeSelectionModal)

      return
    }

    setActiveModal(GenerateLabelModal.GetShippingLabelModal)
  }, [
    openModal,
    labelOptions,
    setActiveModal,
    labelOptionsError,
    labelOptionsUiState,
    labelOptionsRewrite,
    hasReusablePackaging,
    trackDropOffTypeAndPackagingRewriteAbTestExposure,
    isDropOffAndLabelTypeSelectionAbTestOn,
  ])

  const handleSetActiveModal = (modal: GenerateLabelModal) => () => setActiveModal(modal)

  const getCollectionDates = () => {
    if (!confirmedCollectionDate?.collection) return undefined

    const collectionDate = confirmedCollectionDate.collection
    const collectionTimeFrom = confirmedCollectionTime?.collection
    const collectionTimeTo = confirmedCollectionTime?.collectionTo

    return {
      date: collectionDate,
      time: {
        from: collectionTimeFrom,
        to: collectionTimeTo,
      },
    }
  }

  const getDropOffTypeKeyValue = () => {
    if (labelOptionsRewrite) return null

    if (!selectedDropOffTypeKey && isMyOwnPackagingExists) return DropOffTypeKey.MyOwnPackage

    return selectedDropOffTypeKey
  }

  const handleLabelGeneration = async () => {
    if (!transactionId) return
    if (!defaultShippingAddress) return

    setIsShippingLabelLoading(true)

    const pickUpDate = getCollectionDates()
    const digitalLabelType = labelOptions ? selectedLabelType : undefined
    const dropOffTypeKeyValue = getDropOffTypeKeyValue()
    const reusablePackage =
      labelOptionsRewrite &&
      hasReusablePackaging &&
      isReusablePackagingFlowEnabled(labelOptionsRewrite)
        ? selectedDropOffTypeKey === PackagingTypes.ReusablePackage
        : undefined

    const dropOffPointType = getDropOffPointType(
      labelOptions,
      labelOptionsRewrite,
      selectedDropOffTypeKey,
      selectedDropOffPointType,
    )
    const labelType = labelOptionsRewrite ? selectedLabelType : undefined

    const response = await orderShippingLabel({
      transactionId,
      sellerAddressId: defaultShippingAddress.id,
      pickUpDate,
      digitalLabelType,
      dropOffTypeKey: dropOffTypeKeyValue,
      selectedDropOffPointType: dropOffPointType,
      reusablePackageSelected: reusablePackage,
      labelType,
    })

    setIsShippingLabelLoading(false)

    if (response.code === ResponseCode.TaxpayerSellingBlocked) {
      setActiveModal(GenerateLabelModal.TaxpayerRestrictionModal)

      return
    }

    if ('errors' in response) {
      setActiveModal(GenerateLabelModal.GenericErrorModal)

      return
    }

    clearNearbyDropOffPoints()
    setActiveModal(GenerateLabelModal.None)
  }

  const trackGetShippingLabelClick = () => {
    const targetDetails = { transaction_id: transactionId }
    track(
      clickEvent({
        target: 'get_shipping_label',
        targetDetails: JSON.stringify(targetDetails),
      }),
    )
  }

  const trackReturnOrderClick = () => {
    const targetDetails = { transaction_id: transactionId }
    track(
      clickEvent({
        target: 'return_order',
        screen: 'message_reply',
        targetDetails: JSON.stringify(targetDetails),
      }),
    )
  }

  const trackClick = () =>
    action === ConversationActionType.ReturnOrder
      ? trackReturnOrderClick()
      : trackGetShippingLabelClick()

  const handleModalOpen = () => {
    fetchLabelOptionsData()
    trackClick()

    if (isReturnShipment(labelOptionsRewrite) || action === ConversationActionType.ReturnOrder) {
      fetchComplaint()
      setActiveModal(GenerateLabelModal.ReturnTheOrder)

      return
    }

    setOpenModal(true)
  }

  const handleReturnShipmentOptionsContinue = () => {
    if (isOldDropOffPointTypeFlow(labelOptions)) {
      return setActiveModal(GenerateLabelModal.DropOffPointTypeSelectionModal)
    }

    if (labelOptionsRewrite) {
      const hasMultipleLabelTypes = hasMultipleLabelTypesAcrossAllDropOffs(
        labelOptionsRewrite,
        isDropOffAndLabelTypeSelectionAbTestOn,
      )
      const hasMultipleDropOffs = isDropOffTypeFlow(labelOptionsRewrite, labelOptions)

      if (hasMultipleDropOffs) {
        return setActiveModal(GenerateLabelModal.DropOffTypeSelectionModal)
      }

      if (hasMultipleLabelTypes) {
        return setActiveModal(GenerateLabelModal.DigitalLabelTypeSelectionModal)
      }
    }

    if (isDropOffTypeFlow(labelOptionsRewrite, labelOptions)) {
      return setActiveModal(GenerateLabelModal.DropOffTypeSelectionModal)
    }

    if (isPackageTypeLabelTypeFlow(labelOptionsRewrite)) {
      return setActiveModal(GenerateLabelModal.DigitalLabelTypeSelectionModal)
    }

    if (isDigitalLabelFlowEnabled(labelOptions, labelOptionsRewrite)) {
      return setActiveModal(GenerateLabelModal.DigitalLabelTypeSelectionModal)
    }

    return setActiveModal(GenerateLabelModal.GetShippingLabelModal)
  }

  const handleAddressSubmit = () => {
    if (shipmentId && confirmedCollectionDate) {
      fetchCollectionDates({ shipmentId })
    }
    fetchDefaultShippingAddress()
    setActiveModal(GenerateLabelModal.GetShippingLabelModal)
  }

  const handleErrorDialogClose = () => {
    handleSetActiveModal(GenerateLabelModal.None)
    reloadPage()
  }

  const handleOpenDropOffPointsFromDropOffTypes = () => {
    setOpenModal(true)
    setModalOrigin(ModalOrigin.DropOffTypeSelection)
    setActiveModal(GenerateLabelModal.DropOffPointMapModal)
  }

  const handleOpenDropOffPointsFromGenerateLabel = () => {
    setOpenModal(true)
    setModalOrigin(ModalOrigin.GenerateLabel)
    setActiveModal(GenerateLabelModal.DropOffPointMapModal)
  }

  const handleDropOffPointModalClose = () => {
    if (modalOrigin === ModalOrigin.DropOffTypeSelection) {
      setActiveModal(GenerateLabelModal.DropOffTypeSelectionModal)
    } else if (modalOrigin === ModalOrigin.GenerateLabel) {
      setActiveModal(GenerateLabelModal.GetShippingLabelModal)
    }
  }

  const renderShippingLabel = () => {
    if (!transactionId) return null

    const isSeller = userSide === TransactionSide.Seller

    return (
      <>
        <GenerateShippingLabelModal
          isGetShippingLabelModalOpen={activeModal === GenerateLabelModal.GetShippingLabelModal}
          onGenerateLabelClick={handleLabelGeneration}
          isShippingLabelLoading={isShippingLabelLoading}
          onChooseDropOffPointOpen={handleOpenDropOffPointsFromGenerateLabel}
        />
        <DropOffTypeSelectionModal
          isOpen={activeModal === GenerateLabelModal.DropOffTypeSelectionModal}
          onClose={handleSetActiveModal(GenerateLabelModal.None)}
          onSuccess={handleSetActiveModal(GenerateLabelModal.GetShippingLabelModal)}
          onShowDropOffPointDialog={handleOpenDropOffPointsFromDropOffTypes}
        />
        <DropOffTypeConfirmationModal
          isOpen={activeModal === GenerateLabelModal.ConfirmationModal}
          onSuccess={handleLabelGeneration}
          onGoBack={handleSetActiveModal(GenerateLabelModal.GetShippingLabelModal)}
        />
        <AddressModal
          isVisible={activeModal === GenerateLabelModal.AddressModal}
          address={defaultShippingAddress || null}
          configuration={addressFieldConfiguration}
          transactionId={transactionId}
          shippingOrderId={labelOptionsData?.shippingOrderId}
          onClose={handleSetActiveModal(GenerateLabelModal.GetShippingLabelModal)}
          onSubmit={handleAddressSubmit}
        />
        <ShippingContactModal
          shipmentId={shipmentId}
          type={isSeller ? ShippingContactType.Seller : ShippingContactType.Buyer}
          transactionId={transactionId}
          phoneNumber={labelOptionsData?.sender.phoneNumber}
          shippingOrderId={labelOptionsData?.shippingOrderId}
          isOpen={activeModal === GenerateLabelModal.ShippingContactModal}
          onClose={handleSetActiveModal(GenerateLabelModal.GetShippingLabelModal)}
        />
        <CourierPickUpDateSelectionModal
          isOpen={activeModal === GenerateLabelModal.CourierPickUpDateSelectionModal}
        />
        <LabelTypeSelectionModal
          isOpen={activeModal === GenerateLabelModal.DigitalLabelTypeSelectionModal}
        />
        <DropOffPointModal
          isOpen={activeModal === GenerateLabelModal.DropOffPointMapModal}
          onClose={handleDropOffPointModalClose}
        />
        {action === ConversationActionType.ReturnOrder && (
          <ReturnShippingOptionsModal
            isOpen={activeModal === GenerateLabelModal.ReturnTheOrder}
            complaint={complaint}
            transactionId={transactionId}
            isBundle={isBundle}
            isComplaintLoading={isComplaintLoading}
            onClose={handleSetActiveModal(GenerateLabelModal.None)}
            onContinue={handleReturnShipmentOptionsContinue}
          />
        )}
        <TaxpayerRestrictionInfoModal
          show={activeModal === GenerateLabelModal.TaxpayerRestrictionModal}
          isSellingBlocked
          screen="message_reply"
          onClose={handleSetActiveModal(GenerateLabelModal.GetShippingLabelModal)}
        />
        <CarrierChangeModal
          show={activeModal === GenerateLabelModal.CarrierChangeModal}
          onClose={handleSetActiveModal(GenerateLabelModal.GetShippingLabelModal)}
          onSubmit={handleSetActiveModal(GenerateLabelModal.None)}
        />
        <DropOffPointTypeSelectionDialog
          isOpen={activeModal === GenerateLabelModal.DropOffPointTypeSelectionModal}
        />
        <GenericErrorModal
          isShown={activeModal === GenerateLabelModal.GenericErrorModal}
          onClose={handleErrorDialogClose}
        />
      </>
    )
  }

  const renderGetShippingLabelFlow = () => {
    return (
      <EnsureBalanceActivated
        title={title}
        action={action}
        primary={primary}
        size={size}
        theme={theme}
        inverse={inverse}
        onSuccess={handleModalOpen}
      >
        <>
          <ConversationActionButton
            primary={primary}
            testId="get-shipping-label-action-button"
            text={title}
            size={size}
            theme={theme}
            inverse={inverse}
            onClick={handleModalOpen}
            isLoading={labelOptionsUiState === UiState.Pending}
          />
          {renderShippingLabel()}
        </>
      </EnsureBalanceActivated>
    )
  }

  if (!transactionId) return null

  return renderGetShippingLabelFlow()
}

export default GetShippingLabel
